<template>
  <Page :page-name="'Custom Field Manager'">
    <div class="mt-4 w-full flex flex-col m-auto" style="height: 90vh">
      <div style="width: 100%; flex: 1 1 auto">
        <ag-grid-vue
          theme="legacy"
          :rowData="fields"
          :columnDefs="colDefs"
          :defaultColDef="defaultColDefs"
          :autoSizeStrategy="{ type: 'fitGridWidth' }"
          :suppressCellFocus="true"
          class="mx-4 py-3 h-full"
          :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
          :singleClickEdit="true"
          :stopEditingWhenCellsLoseFocus="true"
          :suppressDragLeaveHidesColumns="true"
          :pinnedBottomRowData="[{}]"
          :isFullWidthRow="(params: IsFullWidthRowParams) => params.rowNode.rowPinned === 'bottom'"
          :fullWidthCellRenderer="CustomAddNewCellRenderer"
          :fullWidthCellRendererParams="{
            callback: () => toggleMode('Add'),
            label: 'value',
            display: true,
          }"
          @row-data-updated="resize"
          @grid-size-changed="resize"
          @cell-editing-stopped="updateCustomField"
          @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
        >
        </ag-grid-vue>
      </div>
      <TSDialog v-if="mode" label="Add Custom Field" v-model:visible="displayModal" modal v-on:after-hide="modalClose" size="w-[600px]!">
        <template #content>
          <AddCustomField @submitted="modalClose" />
        </template>
      </TSDialog>
    </div>
  </Page>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { usePropsStore } from "@/stores/props";
import { CustomFieldListSchema, type CustomField } from "@/types/CustomField";
import CustomAddNewCellRenderer from "@/components/cell-renderers/CustomAddNewCellRenderer.vue";
import { CustomFieldScope } from "@/enums/CustomFieldScope";
import { CustomFieldDataType } from "@/enums/CustomFieldDataType";
import CustomTagRenderer from "@/components/cell-renderers/CustomTagRenderer.vue";
import { CustomTagType } from "@/enums/CustomTagType";
import CustomCheckboxRenderer from "@/components/cell-renderers/CustomCheckboxRenderer.vue";
import type { GridApi, GridReadyEvent, GridSizeChangedEvent, IsFullWidthRowParams, RowDataUpdatedEvent, ValueGetterParams } from "ag-grid-community";

const gridApi = ref<GridApi>();
const mode = ref("");
const displayModal = ref(false);
const fields = ref<CustomField[]>([]);

const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

onMounted(async (): Promise<void> => {
  populateFields();
});

const populateFields = (): void => {
  fields.value = [];

  axios.get("/customfields").then((response) => {
    handleApiResponse(
      response,
      (result: any) => {
        parseData(CustomFieldListSchema, result, fields);
      },
      (errors: ApiError[]) => {
        error("Could not retrieve custom fields", errors[0].message);
      }
    );
  });
};

const toggleMode = (value: string): void => {
  mode.value = value;
  displayModal.value = value.length !== 0;
};

const modalClose = (): void => {
  toggleMode("");
  populateFields();
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const updateCustomField = (event: any): void => {
  if (event.newValue === event.oldValue) return;

  if (event.colDef.field === "label") {
    setCustomFieldLabel(event);
  }
};

const toggleCustomFieldActive = (index: number, checked: boolean): void => {
  const dataRow = gridApi.value?.getDisplayedRowAtIndex(index);
  if (dataRow == null) return;
  dataRow.setDataValue("active", checked);

  const customField: CustomField = dataRow.data;

  const data = new FormData();
  data.append("id", customField.id + "");
  data.append("active", customField.active ? "true" : "false");

  axios
    .post("/customfields/toggle", data)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", "Custom field " + (customField.active ? "activated" : "deactivated"));
        },
        (errors: ApiError[]) => {
          error(errors[0].message, "");
          dataRow.setDataValue("active", !customField.active);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const setCustomFieldLabel = (event: any): void => {
  const customField: CustomField = event.data;

  const data = new FormData();
  data.append("id", customField.id + "");
  data.append("label", event.newValue);

  axios
    .post("/customfields/edit", data)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", "Custom field display name updated");
        },
        (errors: ApiError[]) => {
          error("Error", errors[0].message);
          const row = gridApi.value?.getDisplayedRowAtIndex(event.rowIndex);
          if (row != null) row.setDataValue("label", event.oldValue);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const defaultColDefs = {
  filter: true,
  floatingFilter: false,
  cellDataType: "text",
};

const colDefs = ref([
  {
    field: "active",
    headerName: "Active",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    maxWidth: 90,
    cellRenderer: CustomCheckboxRenderer,
    cellRendererParams: () => {
      return {
        setValue: toggleCustomFieldActive,
        disabled: false,
      };
    },
  },
  {
    field: "scope",
    headerName: "Scope",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    valueGetter: (params: ValueGetterParams) => CustomFieldScope[params.data.scope],
    cellRenderer: CustomTagRenderer,
    cellRendererParams: {
      type: CustomTagType.CUSTOM_FIELD_SCOPE,
    },
    maxWidth: 150,
  },
  {
    headerName: "Data Type",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    valueGetter: (params: ValueGetterParams) => CustomFieldDataType[params.data.dataType],
    cellRenderer: CustomTagRenderer,
    cellRendererParams: {
      type: CustomTagType.CUSTOM_FIELD_DATA_TYPE,
    },
    maxWidth: 150,
  },
  {
    field: "label",
    headerName: "Display Name",
    editable: true,
    cellEditorParams: { maxLength: 250 },
  },
]);
</script>
